import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import Modal from "components/basic/modal";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import TextArea from "components/basic/textArea";
import AutoComplete from "components/basic/autoComplete";
import LoadingSpinner from "components/basic/loadingSpinner";
import { useSnack } from "hooks/useSnack";

import { BookingType } from "../../../eventTypes";
import { DateField } from "../../../setAvailability/pickers";
import TimeSlotPicker from "../../../../meetings/components/TimeSlotPicker";
import {
  Slot,
  earlierDate,
  horizonMaxDate,
  earliestBookableDate,
} from "../../../../meetings/helpers/availabilitySlots";
import {
  BookableQuote,
  BookableVendor,
  CUSTOMER_REQUEST_TYPES,
  REQUEST_SLOT_MINUTES,
  quoteMaxDate,
  useQuoteVendorAvailability,
  useRequestBooking,
} from "./bookAppointmentApi";

interface BookAppointmentModalProps {
  // One entry per vendor. A single vendor with a single quote skips both
  // pickers (e.g. when opened from a specific quote).
  vendors: BookableVendor[];
  // Preselected date (e.g. the clicked calendar day); past dates fall back
  // to today.
  defaultDate?: string;
  onClose: () => void;
}

const BookAppointmentModal: React.FC<BookAppointmentModalProps> = ({
  vendors,
  defaultDate,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calendar");
  const showSnack = useSnack();

  const [selectedVendor, setSelectedVendor] = useState<BookableVendor | null>(
    vendors.length === 1 ? vendors[0] : null,
  );
  const [selectedQuote, setSelectedQuote] = useState<BookableQuote | null>(
    vendors.length === 1 && vendors[0].quotes.length === 1
      ? vendors[0].quotes[0]
      : null,
  );

  const { loading, availabilityConfigured, availability, busyBookings } =
    useQuoteVendorAvailability(selectedQuote?.quoteId);
  const { request, requesting } = useRequestBooking();

  const today = moment().format("YYYY-MM-DD");
  const [type, setType] = useState<BookingType>("video");
  const [date, setDate] = useState(
    defaultDate && defaultDate >= today ? defaultDate : today,
  );
  const [slot, setSlot] = useState<Slot | null>(null);
  const [description, setDescription] = useState("");

  // The visit has to sit inside both the vendor's booking window and the life
  // of the estimate it is about.
  const expiryMaxDate = quoteMaxDate(selectedQuote);
  const maxDate = earlierDate(
    availability ? horizonMaxDate(availability.bookingHorizon) : undefined,
    expiryMaxDate,
  );
  const limitedByExpiry = Boolean(maxDate && maxDate === expiryMaxDate);

  const handleSubmit = async () => {
    if (!slot || !selectedQuote) {
      return;
    }
    const result = await request({
      quoteId: selectedQuote.quoteId,
      type,
      date,
      fromTime: slot.from,
      toTime: slot.to,
      description: description.trim() || undefined,
    });
    if (result.ok) {
      showSnack(t("book.requested"), "success");
      onClose();
    } else {
      showSnack(result.error ?? t("snack.request_failed"), "error");
    }
  };

  const vendorName = selectedVendor?.vendorName ?? "the vendor";

  return (
    <Modal onClose={onClose}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box
          sx={{
            width: { xs: "88vw", sm: 440 },
            maxHeight: "86vh",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              mb: 2,
            }}
          >
            <Typography variant="h4" style={{ fontWeight: 600 }}>
              {t("book.title")}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ position: "absolute", right: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {vendors.length === 0 ? (
            <Typography variant="body2">{t("book.no_quotes")}</Typography>
          ) : (
            <Stack spacing={2}>
              {/* Vendor picker (only when opened without a specific quote) */}
              {vendors.length > 1 && (
                <AutoComplete
                  label={t("book.vendor")}
                  options={vendors.map((v) => ({
                    id: v.vendorId,
                    label: v.vendorName,
                  }))}
                  value={selectedVendor?.vendorId ?? null}
                  onChange={(value) => {
                    const vendor = value
                      ? (vendors.find((v) => v.vendorId === value.id) ?? null)
                      : null;
                    setSelectedVendor(vendor);
                    // A single quote needs no second choice.
                    setSelectedQuote(
                      vendor && vendor.quotes.length === 1
                        ? vendor.quotes[0]
                        : null,
                    );
                    setSlot(null);
                  }}
                />
              )}

              {/* Quote picker (when the chosen vendor sent several quotes) */}
              {selectedVendor && selectedVendor.quotes.length > 1 && (
                <AutoComplete
                  label={t("book.quote")}
                  options={selectedVendor.quotes.map((q) => ({
                    id: q.quoteId,
                    label: q.label,
                  }))}
                  value={selectedQuote?.quoteId ?? null}
                  onChange={(value) => {
                    setSelectedQuote(
                      value
                        ? (selectedVendor.quotes.find(
                            (q) => q.quoteId === value.id,
                          ) ?? null)
                        : null,
                    );
                    setSlot(null);
                  }}
                />
              )}

              {!selectedVendor ? (
                <Typography
                  variant="caption"
                  style={{ color: theme.palette.text.secondary }}
                >
                  {t("book.choose_vendor")}
                </Typography>
              ) : !selectedQuote ? (
                <Typography
                  variant="caption"
                  style={{ color: theme.palette.text.secondary }}
                >
                  {t("book.choose_quote")}
                </Typography>
              ) : loading ? (
                <LoadingSpinner />
              ) : !availabilityConfigured || !availability ? (
                <Typography variant="body2">
                  <RichText
                    t={t}
                    i18nKey="book.no_booking"
                    values={{ vendor: vendorName }}
                    components={{ strong: <strong /> }}
                  />
                </Typography>
              ) : (
                <>
                  <Typography
                    variant="caption"
                    style={{ color: theme.palette.text.secondary }}
                  >
                    {t("book.pick_slot", {
                      minutes: REQUEST_SLOT_MINUTES,
                      vendor: vendorName,
                    })}
                  </Typography>

                  {/* Without this the cut-off date reads as an arbitrary
                      greyed-out calendar. */}
                  {limitedByExpiry && (
                    <Typography
                      variant="caption"
                      style={{ color: theme.palette.text.secondary }}
                    >
                      {t("book.dates_stop", {
                        date: moment(maxDate).format("MMM D"),
                      })}
                    </Typography>
                  )}

                  {/* Meeting type */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {CUSTOMER_REQUEST_TYPES.map((t) => {
                      const Icon = t.icon;
                      const active = type === t.id;
                      return (
                        <Box
                          key={t.id}
                          component="button"
                          type="button"
                          onClick={() => setType(t.id)}
                          sx={{
                            flex: 1,
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                            py: 1,
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            border: `1px solid ${
                              active ? t.color : theme.palette.divider
                            }`,
                            backgroundColor: active
                              ? `${t.color}1a`
                              : theme.palette.background.paper,
                            fontWeight: active ? 600 : 400,
                            "&:hover": { borderColor: t.color },
                          }}
                        >
                          <Icon fontSize="small" sx={{ color: t.color }} />
                          {t.label}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Date */}
                  <DateField
                    value={date}
                    onChange={(d) => {
                      setDate(d);
                      setSlot(null);
                    }}
                    minDate={earliestBookableDate(availability)}
                    maxDate={maxDate}
                  />

                  {/* Time slots */}
                  <TimeSlotPicker
                    date={date}
                    durationMinutes={REQUEST_SLOT_MINUTES}
                    availability={availability}
                    bookings={busyBookings}
                    value={slot?.from}
                    onSelect={setSlot}
                  />

                  {/* Optional message */}
                  <TextArea
                    placeHolder={`Anything ${vendorName} should know? (optional)`}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />

                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!slot}
                    loading={requesting}
                    fullWidth
                  >
                    {t("book.request")}
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Box>
      </LocalizationProvider>
    </Modal>
  );
};

export default BookAppointmentModal;
